import React, { useContext, useEffect, useState } from "react";
import "./Details.css";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";
import { translations } from "../../../utils/lang";
import { Button, Input, Checkbox, Select } from "../../ui";

const fieldLabels = [
  "businessNameEn",
  "bankName",
  "bankBeneficiaryName",
  "IBANnum",
  "salesPhone",
  "salesEmail",
  "accountantPhone",
  "accountantEmail",
  "integrationName",
  "posName",
];

const importantCheckboxFields = [
  "commercialRegister",
  "taxCertificate",
  "menuPdf",
  "restaurantLogo",
];

const checkboxFields = [
  "ibanCertificate",
  "shopLicense",
  "nationalAddress",
  "ludMenu",
];

const normalizeIntegrationData = (value = {}) => ({
  ...value,

  // توافق مع المطاعم القديمة:
  // إذا لم يكن hasIntegration محفوظًا ولكن يوجد اسم تكامل،
  // نعتبر أن المطعم لديه تكامل.
  hasIntegration:
    Boolean(value.hasIntegration) ||
    Boolean(String(value.integrationName || "").trim()),
});

export const MoreDetails = ({ data, onSave, restaurantId }) => {
  const { lang } = useContext(Context);
  const { can } = useAccess();

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(() => normalizeIntegrationData(data));

  const hasIntegration =
    Boolean(form.hasIntegration) ||
    Boolean(String(form.integrationName || "").trim());

  useEffect(() => {
    setForm(normalizeIntegrationData(data));
  }, [data]);

  function isObjectEqualCaseSensitive(obj1, obj2) {
    const keys1 = Object.keys(obj1 || {});
    const keys2 = Object.keys(obj2 || {});

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!Object.prototype.hasOwnProperty.call(obj2, key)) return false;
      if (obj1[key] !== obj2[key]) return false;
    }

    return true;
  }

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    if (!can("restaurant.details.update", { restaurantId })) return;
    const normalizedForm = normalizeIntegrationData(form);
    const normalizedData = normalizeIntegrationData(data);

    if (!isObjectEqualCaseSensitive(normalizedForm, normalizedData)) {
      onSave(normalizedForm);
    }

    setForm(normalizedForm);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setForm(normalizeIntegrationData(data));
    setIsEditing(false);
  };

  return (
    <>
      <div className="more-details-layout">
        <div className="important-fields">
          {importantCheckboxFields.map((key) => (
            <div className="detail-row" key={key}>
              {isEditing ? (
                <Checkbox
                  checked={!!form[key]}
                  onChange={(event) =>
                    handleChange(key, event.target.checked)
                  }
                  label={
                    translations[lang].restaurants.restaurantsRow
                      .moreDetails?.[key] || key
                  }
                />
              ) : (
                <div className="moredetails-row">
                  <div
                    className={
                      form[key]
                        ? "details-approved"
                        : "detailes-not-approved"
                    }
                  />

                  <strong>
                    {translations[lang].restaurants.restaurantsRow
                      .moreDetails?.[key] || key}
                  </strong>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="other-fields">
          <div className="detail-row">
            <p>{lang === "ar" ? "يوجد تكامل" : "Has integration"}:</p>

            {isEditing ? (
              <Checkbox
                checked={hasIntegration}
                onChange={(event) =>
                  handleChange("hasIntegration", event.target.checked)
                }
                label={lang === "ar" ? "نعم" : "Yes"}
              />
            ) : (
              <div className="moredetails-row">
                <span>
                  {hasIntegration
                    ? lang === "ar"
                      ? "نعم"
                      : "Yes"
                    : lang === "ar"
                      ? "لا"
                      : "No"}
                </span>
              </div>
            )}
          </div>

          {hasIntegration && (
            <div className="detail-row">
              <p>{lang === "ar" ? "نوع الربط" : "Connection type"}:</p>

              {isEditing ? (
                <Select
                  value={form.integrationType || ""}
                  onChange={(event) =>
                    handleChange("integrationType", event.target.value)
                  }
                  options={[
                    {
                      value: "",
                      label: lang === "ar" ? "اختر" : "Select",
                    },
                    {
                      value: "pos",
                      label:
                        lang === "ar" ? "نظام كاشير" : "POS system",
                    },
                    {
                      value: "external_tablet",
                      label:
                        lang === "ar"
                          ? "تابلت خارجي"
                          : "External tablet",
                    },
                  ]}
                />
              ) : (
                <div className="moredetails-row">
                  <span>
                    {form.integrationType === "pos"
                      ? lang === "ar"
                        ? "نظام كاشير"
                        : "POS system"
                      : form.integrationType === "external_tablet"
                        ? lang === "ar"
                          ? "تابلت خارجي"
                          : "External tablet"
                        : "-"}
                  </span>
                </div>
              )}
            </div>
          )}

          {fieldLabels
            .filter((key) => {
              if (
                !hasIntegration &&
                ["integrationName", "posName"].includes(key)
              ) {
                return false;
              }

              if (form.integrationType !== "pos" && key === "posName") {
                return false;
              }

              return true;
            })
            .map((key) => (
              <div className="detail-row" key={key}>
                <p>
                  {translations[lang].restaurants.restaurantsRow
                    .moreDetails?.[key] || key}
                  :
                </p>

                {isEditing ? (
                  <Input
                    value={form[key] || ""}
                    onChange={(event) =>
                      handleChange(key, event.target.value)
                    }
                  />
                ) : (
                  <div className="moredetails-row">
                    {!form[key] && (
                      <div className="detailes-not-approved" />
                    )}

                    <span>{form[key] || "-"}</span>
                  </div>
                )}
              </div>
            ))}
        </div>

        <div className="other-fields2">
          {checkboxFields.map((key) => (
            <div className="detail-row" key={key}>
              {isEditing ? (
                <Checkbox
                  checked={!!form[key]}
                  onChange={(event) =>
                    handleChange(key, event.target.checked)
                  }
                  label={
                    translations[lang].restaurants.restaurantsRow
                      .moreDetails?.[key] || key
                  }
                />
              ) : (
                <div className="moredetails-row">
                  <div
                    className={
                      form[key]
                        ? "details-approved"
                        : "detailes-not-approved"
                    }
                  />

                  <p>
                    {translations[lang].restaurants.restaurantsRow
                      .moreDetails?.[key] || key}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {can("restaurant.details.update", { restaurantId }) && (
        <div className="details-actions">
          {isEditing ? (
            <>
              <Button
                type="button"
                variant="primary"
                appearance="filled"
                fullWidth
                onClick={handleSave}
              >
                {translations[lang].restaurants.restaurantsRow.details.save}
              </Button>

              <Button
                type="button"
                variant="danger"
                appearance="soft"
                fullWidth
                onClick={handleCancel}
              >
                {translations[lang].restaurants.restaurantsRow.details.cancel}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="primary"
              appearance="soft"
              fullWidth
              onClick={() => setIsEditing(true)}
            >
              {translations[lang].restaurants.restaurantsRow.details.edit}
            </Button>
          )}
        </div>
      )}
    </>
  );
};